import { useEffect, useState } from "react";
import { FaWhatsapp } from "react-icons/fa";
import { useLocation } from "wouter";

import { useConsent } from "@/components/ConsentContext";
import { cn } from "@/lib/utils";
import {
  trackWhatsAppClick,
  WHATSAPP_URL,
  type WhatsAppLinkLocation,
} from "@/lib/whatsapp";

type WhatsAppLinkProps = {
  variant: "floating" | "contact";
  linkLocation: WhatsAppLinkLocation;
  className?: string;
};

export default function WhatsAppLink({
  variant,
  linkLocation,
  className,
}: WhatsAppLinkProps) {
  const { hasChoice, settingsOpen } = useConsent();
  const [currentPath] = useLocation();
  const [contactSectionVisible, setContactSectionVisible] = useState(false);

  useEffect(() => {
    if (variant !== "floating") return;

    const contactSection = document.getElementById("kontakt");
    if (!contactSection) {
      setContactSectionVisible(false);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => setContactSectionVisible(entry.isIntersecting),
      { threshold: 0 },
    );
    observer.observe(contactSection);

    return () => observer.disconnect();
  }, [currentPath, variant]);

  if (
    variant === "floating" &&
    (!hasChoice || settingsOpen || contactSectionVisible)
  ) {
    return null;
  }

  return (
    <a
      href={WHATSAPP_URL}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="TTS Britz über WhatsApp kontaktieren"
      title="Per WhatsApp anfragen"
      onClick={() => trackWhatsAppClick(linkLocation)}
      className={cn(
        "inline-flex min-h-11 items-center justify-center gap-2 rounded-full bg-[#25D366] font-semibold text-white shadow-[0_10px_28px_rgba(0,0,0,0.18)] transition-[transform,background-color,box-shadow] duration-200 hover:-translate-y-0.5 hover:bg-[#1fbd59] hover:shadow-[0_14px_34px_rgba(0,0,0,0.22)] focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-[#0b6b31] active:translate-y-0 motion-reduce:transform-none",
        variant === "floating" &&
          "whatsapp-floating fixed z-40 min-h-[52px] min-w-[52px] p-3.5 md:min-h-14 md:min-w-0 md:px-5",
        variant === "contact" && "w-full px-5 py-3 text-base",
        className,
      )}
    >
      <FaWhatsapp className="h-6 w-6 shrink-0" aria-hidden="true" />
      <span className={variant === "floating" ? "hidden md:inline" : undefined}>
        Per WhatsApp anfragen
      </span>
    </a>
  );
}
